<template>
    <SrModalPage
        v-model="currentStepNumber"
        type="stepper"
        :tabs="tabs"
        :show.sync="showModal"
        :steps="steps"
        v-bind="$attrs"
        :loading="showLoading"
        :loading-config="loadingConfig"
        :can-back="canBack"
        :can-continue="canContinue"
        :can-submit="allStepsValid && submitEnabled"
        :disable-keyboard-navigation="disableKeyboardNavigation"
        :disable-keyboard-submit="disableKeyboardSubmit"
        @continue-clicked="nextSlide"
        @close="close"
        @submit="submit"
    >
        <!-- pass slots to child component -->
        <slot v-for="(_, name) in $slots" :slot="name" :name="name" />

        <SrModalPageFormFooter
            v-if="!tabs"
            slot="footer"
            ref="footer"
            :is-close-loading="isCloseLoading"
            :loading="isContinueLoading"
            :can-continue="isLastSlide ? canContinue && submitEnabled : canContinue"
            :continue-text="isLastSlide ? submitText : continueText"
            :can-back="canBack"
            :can-close="canClose"
            :close-text="closeText"
            :back-text="backText"
            :show-back-button="showBackButton"
            :show-close-button="showCloseButton"
            :show-continue-button="(isLastSlide && showSubmitButton) || !isLastSlide"
            @continue-clicked="nextSlide"
            @close-modal-clicked="closeModalPage"
            @back-clicked="previousSlide"
        />
        <SrModalPageFormTabFooter
            v-if="tabs"
            slot="footer"
            :can-submit="allStepsValid && submitEnabled"
            :show-submit="showSubmitButton"
            :loading="showLoading"
            @submit-clicked="submit"
        />
    </SrModalPage>
</template>

<script>
import SrModalPage from '../SrModalPage/SrModalPage';
import SrModalPageFormFooter from './SrModalPageFormFooter';
import SrModalPageFormTabFooter from './SrModalPageFormTabFooter';

/**
 * This component is a wrapper for SrModalPage with additional features:
 * - A button in the header allows the user to step through each slide and submit at the end.
 * - Rules for continue button to be enabled (steps: Array<{..., continueRules: Array<Function>}>)
 */
export default {
    name: 'SrModalPageForm',
    components: { SrModalPageFormFooter, SrModalPage, SrModalPageFormTabFooter },
    model: {
        prop: 'value',
        event: 'input',
    },
    props: {
        /**
         * Returns the currently active tab. Starts at 1
         * @model
         */
        value: {
            type: Number,
            default: 1,
        },
        /**
         * Controls visibility of modal. Can be used with .sync modifier
         */
        show: {
            type: Boolean,
            required: true,
        },
        /**
         * List of steps.
         * Can be array of strings or array of objects
         * Objects contain the following properties:
         *  - name              string      required
         *  - rules             Array<Fn>   default: []
         *  - canContinue       boolean     default: true
         *  - completeIcon      string      default: '$complete'
         *  - editIcon          string      default: '$edit'
         *  - errorIcon         string      default: '$error'
         *
         *  rules: Indicates error in header if rules return false
         *  continueRules: Disables 'continue' button if rules return false
         */
        steps: {
            type: Array,
            required: false,
            default: () => [],
        },
        /**
         * Sets text for next-slide button
         */
        continueText: {
            type: String,
            default: 'Continue',
        },
        /**
         * Sets text for submit-button on last slide
         */
        submitText: {
            type: String,
            default: 'Submit',
        },
        /**
         * Sets text for back-slide button
         */
        backText: {
            type: String,
            default: 'Back',
        },
        closeText: {
            type: String,
            default: 'Close',
        },
        /**
         * Clicking outside of the element will not deactivate it.
         */
        persistent: {
            type: Boolean,
            default: true,
        },
        /**
         * Shows "X" button in top right corner to close modal.
         */
        closeAble: {
            type: Boolean,
            default: true,
        },
        /**
         * Shows loading animation
         */
        loading: {
            type: Boolean,
            default: false,
        },
        /**
         * Custom loading configuration for visualizing progress
         */
        loadingConfig: {
            type: Object,
            default: () => ({
                indeterminate: true,
                progress: 0, // 0.0 - 1.0
            }),
        },
        /**
         * Shows back button
         */
        showBackButton: {
            type: Boolean,
            default: true,
        },
        showSubmitButton: {
            type: Boolean,
            default: true,
        },
        /**
         * Shows tabs-View of steppers
         */
        tabs: {
            type: Boolean,
            default: false,
        },
        closeCallback: {
            type: Function,
            default: null,
        },
        submitEnabled: {
            type: Boolean,
            default: true,
        },
        /**
         * Disables 'ArrowLeft' and 'ArrowRight' navigation between tabs
         */
        disableKeyboardNavigation: {
            type: Boolean,
            default: false,
        },
        /**
         * Disables 'CTRL + S' for submitting the form
         */
        disableKeyboardSubmit: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            showModal: this.show,
            currentStepNumber: this.value,
            submitIsLoading: false,
            closeIsLoading: false,
            continueIsLoading: false,
        };
    },
    computed: {
        isFirstSlide() {
            return this.currentStepNumber === 1;
        },
        isLastSlide() {
            return this.currentStepNumber === this.steps.length;
        },
        canContinue() {
            if (typeof this.currentStep !== 'object') {
                return true;
            }

            if (typeof this.currentStep.canContinue === 'boolean') {
                return this.currentStep.canContinue;
            }

            return true;
        },
        canBack() {
            return !this.isFirstSlide && this.showBackButton;
        },
        showCloseButton() {
            return typeof this.currentStep.canClose === 'boolean';
        },
        canClose() {
            const shouldBeAbleToClose =
                this.currentStep.canContinue || this.allStepsValid || typeof this.currentStep.close !== 'function';
            if (typeof this.currentStep.canClose === 'boolean' && shouldBeAbleToClose) {
                return this.currentStep.canClose;
            }

            return false;
        },
        showLoading() {
            return this.loading || this.submitIsLoading || this.continueIsLoading || this.isCloseLoading;
        },
        isContinueLoading() {
            return this.loading || this.submitIsLoading || this.continueIsLoading;
        },
        isCloseLoading() {
            return Boolean(this.closeIsLoading);
        },
        allStepsValid() {
            return !this.steps.some((step) => step.canContinue === false);
        },
        currentStep() {
            return this.steps[this.currentStepNumber - 1];
        },
    },
    watch: {
        value() {
            this.currentStepNumber = this.value;
        },
        currentStepNumber() {
            this.$emit('input', this.currentStepNumber); // update components v-model
        },
        show() {
            this.showModal = this.show;
        },
        showModal() {
            this.$emit('update:show', this.showModal); // update "show" property
        },
    },
    methods: {
        close() {
            this.$emit('close');
            if (this.closeCallback) {
                return;
            }
            this.showModal = false;
        },
        nextSlide() {
            if (this.isLastSlide) {
                this.submit();
            } else if (typeof this.currentStep.continue === 'function') {
                this.continueIsLoading = true;
                this.currentStep
                    .continue()
                    .then(() => {
                        this.currentStepNumber += 1;
                    })
                    .catch(() => {
                        /* ignore */
                    })
                    .finally(() => {
                        this.continueIsLoading = false;
                    });
            } else {
                this.currentStepNumber += 1;
            }
        },
        previousSlide() {
            this.currentStepNumber -= 1;
        },
        closeModalPage() {
            if (typeof this.currentStep.close === 'function') {
                this.closeIsLoading = true;
                this.currentStep
                    .close()
                    .then(() => {
                        this.close();
                    })
                    .catch(() => {
                        /* ignore */
                    })
                    .finally(() => {
                        this.closeIsLoading = false;
                    });
            } else {
                this.close();
            }
        },
        submit() {
            this.submitIsLoading = true;
            this.$emit('submit', () => {
                this.submitIsLoading = false;
            });
        },
    },
};
</script>
